































import '@/assets/scss/pages/app-calendar.scss';
import { Component, Prop, Watch } from 'vue-property-decorator';
import { mixins } from 'vue-class-component';
import { FormatMixin } from '@/mixins/FormatMixin';
import FullCalendar from '@fullcalendar/vue';
import dayGridPlugin from '@fullcalendar/daygrid';
import listPlugin from '@fullcalendar/list';
import interactionPlugin from '@fullcalendar/interaction';
import { ITask, TaskStatus } from 'admiral-core';
import { EventInput } from '@fullcalendar/core';
import { TaskApi } from '@/services/TaskApi';
import TaskListSingletonMenus from '@/components/tasks/TaskListSingletonMenus.vue';
import type { TaskListSingletonMenuState } from '@/components/tasks/TaskListSingletonMenuState';

@Component({
  name: 'Tasks2Calendar',
  components: {
    FullCalendar,
    TaskListSingletonMenus,
  },
})
export default class TasksCalendar extends mixins(FormatMixin) {
  @Prop({ type: Array, required: true })
  tasks!: ITask[];

  calendarOptions: any = {};

  statusMenuState: TaskListSingletonMenuState | null = null;

  /** Inner remount so removed events disappear; parent must not remount (would reset month to today). */
  fullCalendarKey = 0;

  /** Compare with next `tasks` to detect deletions (same instance; `@Watch` skips initial assign). */
  private previousTaskIdSet: Set<string> | null = null;

  /**
   * Month grid uses one column per weekday — Sat/Sun columns can only share one width. We narrow a
   * weekend column (~half width) when no due task falls on any Saturday / Sunday in the visible range.
   */
  weekendNarrowSat = false;
  weekendNarrowSun = false;

  /** Match tasks2 / list breakpoints: list-first layout on phones and small tablets. */
  get isNarrowScreen(): boolean {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(max-width: 767.98px)').matches;
  }

  created() {
    const narrow = this.isNarrowScreen;
    this.calendarOptions = {
      plugins: [dayGridPlugin, listPlugin, interactionPlugin],
      initialView: narrow ? 'listMonth' : 'dayGridMonth',
      headerToolbar: narrow
        ? {
            left: 'prev,next',
            center: 'title',
            right: 'today,dayGridMonth listMonth',
          }
        : {
            left: 'prev,next today',
            center: 'title',
            right: 'dayGridMonth,listMonth',
          },
      locale: 'de',
      firstDay: 1,
      buttonText: {
        today: 'Heute',
        month: 'Monat',
        list: 'Liste',
      },
      views: {
        dayGridMonth: {
          dayMaxEvents: true,
        },
        listMonth: {
          listDayFormat: { weekday: 'short', day: 'numeric', month: 'short' },
          listDaySideFormat: false,
        },
      },
      events: (fetchInfo: unknown, successCallback: (events: EventInput[]) => void) => {
        successCallback(this.calendarEvents);
      },
      editable: true,
      eventClick: this.handleEventClick,
      eventDrop: this.handleEventDrop,
      eventDidMount: (info: { el: HTMLElement; event: { extendedProps: { task: ITask } } }) =>
        this.onCalendarEventDidMount(info),
      dayCellDidMount: (info: { el: HTMLElement; date: Date }) => this.handleDayCellMount(info),
      height: 'auto',
      windowResize: () => {
        this.$nextTick(() => {
          (this.$refs.fullCalendar as any)?.getApi?.()?.updateSize?.();
        });
      },
      datesSet: () => {
        this.updateWeekendColumnNarrowFlags();
      },
    };
  }

  get calendarEvents(): EventInput[] {
    return this.tasks
      .filter((t) => t.dueAt)
      .map((task) => ({
        id: task.id,
        title: task.title || 'Ohne Titel',
        start: this.toDateStr(task.dueAt),
        extendedProps: { task },
        classNames: this.getEventClassNames(task),
      }));
  }

  toDateStr(date: Date | string | undefined): string {
    if (!date) return '';
    const d = new Date(date);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  getEventClassNames(task: ITask): string[] {
    const tags = task.tags || [];
    const tagIndex = tags.length > 0 ? this.tagIndexForTask(task) : 0;
    const colorName = this.tagClasses[tagIndex % this.tagClasses.length];
    return [`fc-event-tag-${colorName}`];
  }

  tagIndexForTask(task: ITask): number {
    const tags = task.tags || [];
    if (tags.length === 0) return 0;
    const firstTagId = tags[0].id || '';
    let hash = 0;
    for (let i = 0; i < firstTagId.length; i++) {
      hash = (hash << 5) - hash + firstTagId.charCodeAt(i);
      hash |= 0;
    }
    return Math.abs(hash);
  }

  handleEventClick(info: { jsEvent: MouseEvent; event: { extendedProps: { task: ITask } } }) {
    const t = info.jsEvent?.target as HTMLElement | null;
    if (t && (t.closest('.fc-task-status-hit') || t.classList.contains('fc-task-status-hit'))) {
      return;
    }
    const task = info.event.extendedProps.task;
    this.$emit('open-task', task);
  }

  /**
   * Same shapes as {@link TaskStatusIcon} (static HTML for FullCalendar eventDidMount).
   */
  calendarStatusIconSvg(task: ITask): string {
    const status = task.status;
    const base =
      'xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="none" viewBox="0 0 24 24"';
    if (status === TaskStatus.TODO) {
      return `<svg ${base}><circle cx="12" cy="12" r="9" fill="none" stroke="#9ca3af" stroke-width="1.5" /></svg>`;
    }
    if (status === TaskStatus.IN_PROGRESS) {
      return `<svg ${base}><g><circle cx="12" cy="12" r="10" fill="none" stroke="#9ca3af" stroke-width="2" /><path fill="#9ca3af" d="M 12 5 A 7 7 0 0 1 12 19 L 12 5 Z" /></g></svg>`;
    }
    if (status === TaskStatus.IN_WAITING) {
      return `<svg ${base}><circle cx="12" cy="12" r="9" fill="none" stroke="#9ca3af" stroke-width="1.5" stroke-dasharray="3 5" /></svg>`;
    }
    if (status === TaskStatus.COMPLETED) {
      return `<svg ${base}><g><circle cx="12" cy="12" r="9" fill="#3A7BC7" stroke="#2d6ab8" stroke-width="1" /><path stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" fill="none" d="M8 12 L11 15 L16 9" /></g></svg>`;
    }
    if (status === TaskStatus.ARCHIVED) {
      return `<svg ${base}><path fill="none" stroke="#6b7280" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" d="M4 8h16v12a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V8zm3-3h10l1 3H6l1-3z"/><path stroke="#6b7280" stroke-width="1.5" stroke-linecap="round" d="M9 13h6"/></svg>`;
    }
    return `<svg ${base}><circle cx="12" cy="12" r="9" fill="none" stroke="#9ca3af" stroke-width="1.5" /></svg>`;
  }

  onCalendarEventDidMount(info: { el: HTMLElement; event: { extendedProps: { task: ITask } } }) {
    const task = info.event.extendedProps.task;
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'fc-task-status-hit';
    btn.setAttribute('data-task-id', task.id);
    btn.setAttribute('aria-label', 'Status ändern');
    btn.setAttribute('title', 'Status ändern');
    btn.innerHTML = `<span class="fc-task-status-hit-inner" aria-hidden="true">${this.calendarStatusIconSvg(
      task,
    )}</span>`;
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      e.preventDefault();
      this.toggleStatusMenu(task, btn);
    });

    const root = info.el;
    let mount: HTMLElement = root;

    if (root.tagName === 'TR' && root.classList.contains('fc-list-event')) {
      const titleTd = root.querySelector('td.fc-list-event-title') as HTMLElement | null;
      if (titleTd) {
        titleTd.classList.add('fc-list-event-title--with-status-btn');
        mount = titleTd;
      }
    } else {
      root.classList.add('fc-event--has-status-btn');
    }

    if (mount.querySelector('.fc-task-status-hit')) return;
    mount.prepend(btn);
  }

  /** After status change: reload data, refetch events, sync any leftover button SVGs. */
  onCalendarStatusChanged() {
    console.log('onCalendarStatusChanged');
    this.$emit('reload');
    this.$nextTick(() => {
      const api = (this.$refs.fullCalendar as any)?.getApi?.();
      if (api) api.refetchEvents();
      this.$nextTick(() => this.refreshMountedStatusIcons());
    });
  }

  onCalendarDeleteTask(task: ITask) {
    this.statusMenuState = null;
    this.$emit('delete-task', task);
  }

  /** Update status glyphs on existing event buttons from current `tasks` prop. */
  refreshMountedStatusIcons() {
    const root = this.$el as HTMLElement | undefined;
    if (!root?.querySelectorAll) return;
    root.querySelectorAll('.fc-task-status-hit[data-task-id]').forEach((el) => {
      const id = el.getAttribute('data-task-id');
      if (!id) return;
      const task = this.tasks.find((t) => t.id === id);
      if (!task) return;
      const inner = el.querySelector('.fc-task-status-hit-inner');
      if (inner) inner.innerHTML = this.calendarStatusIconSvg(task);
    });
  }

  toggleStatusMenu(task: ITask, anchor: HTMLElement) {
    if (
      this.statusMenuState &&
      this.statusMenuState.task.id === task.id &&
      this.statusMenuState.kind === 'status'
    ) {
      this.statusMenuState = null;
      return;
    }
    this.statusMenuState = { kind: 'status', task, anchor };
  }

  handleDayCellMount(info: { el: HTMLElement; date: Date }) {
    const dayTop = info.el.querySelector('.fc-daygrid-day-top');
    if (!dayTop) return;
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'fc-daygrid-add-task-btn';
    btn.innerHTML = '+';
    btn.setAttribute('aria-label', 'Aufgabe erstellen');
    const date = new Date(info.date);
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      e.preventDefault();
      this.$emit('create-task-with-date', date);
    });
    dayTop.appendChild(btn);
  }

  async handleEventDrop(info: {
    event: { id: string; extendedProps: { task: ITask }; start: Date | null };
    revert: () => void;
  }) {
    const task = info.event.extendedProps.task;
    const newDueAt = info.event.start ? new Date(info.event.start) : null;
    if (!newDueAt) {
      info.revert();
      return;
    }
    try {
      await TaskApi.updateDueDate(task.id, task, newDueAt);
      this.$emit('reload');
      this.$toastr.s('Fälligkeitsdatum wurde aktualisiert.');
    } catch (e) {
      info.revert();
      this.$toastr.e('Fälligkeitsdatum konnte nicht geändert werden.');
    }
  }

  @Watch('tasks', { deep: true })
  onTasksChange() {
    const nextIds = new Set(this.tasks.map((t) => t.id));
    let taskWasRemoved = false;
    if (this.previousTaskIdSet !== null) {
      for (const id of this.previousTaskIdSet) {
        if (!nextIds.has(id)) {
          taskWasRemoved = true;
          break;
        }
      }
    }
    this.previousTaskIdSet = nextIds;

    if (taskWasRemoved) {
      this.$nextTick(() => {
        const api = (this.$refs.fullCalendar as any)?.getApi?.();
        if (api) {
          this.calendarOptions.initialDate = api.getDate();
        }
        this.fullCalendarKey += 1;
        this.$nextTick(() => this.updateWeekendColumnNarrowFlags());
      });
      return;
    }

    this.syncCalendarFromTasks();
  }

  mounted() {
    this.previousTaskIdSet = new Set(this.tasks.map((t) => t.id));
    this.syncCalendarFromTasks();
  }

  private syncCalendarFromTasks() {
    this.$nextTick(() => {
      const api = (this.$refs.fullCalendar as any)?.getApi?.();
      if (api) api.refetchEvents();
      this.$nextTick(() => {
        this.refreshMountedStatusIcons();
        this.updateWeekendColumnNarrowFlags();
      });
    });
  }

  /** See `weekendNarrowSat` — table columns force one width per weekday. */
  private updateWeekendColumnNarrowFlags() {
    const api = (this.$refs.fullCalendar as any)?.getApi?.();
    if (!api?.view) {
      this.weekendNarrowSat = false;
      this.weekendNarrowSun = false;
      return;
    }
    const view = api.view as { type?: string; activeStart?: Date; activeEnd?: Date };
    if (view.type !== 'dayGridMonth' || !view.activeStart || !view.activeEnd) {
      this.weekendNarrowSat = false;
      this.weekendNarrowSun = false;
      return;
    }
    this.updateWeekendNarrowFromRange(view.activeStart, view.activeEnd);
  }

  private updateWeekendNarrowFromRange(activeStart: Date, activeEnd: Date) {
    let anySat = false;
    let anySun = false;
    const d = new Date(activeStart);
    const end = new Date(activeEnd);
    while (d < end) {
      const dow = d.getDay();
      if (this.dayHasDueOnLocalDate(d)) {
        if (dow === 6) anySat = true;
        if (dow === 0) anySun = true;
      }
      d.setDate(d.getDate() + 1);
    }
    this.weekendNarrowSat = !anySat;
    this.weekendNarrowSun = !anySun;
  }

  private dayHasDueOnLocalDate(d: Date): boolean {
    const key = this.toDateStr(d);
    for (const t of this.tasks) {
      if (!t.dueAt) continue;
      if (this.toDateStr(t.dueAt) === key) return true;
    }
    return false;
  }
}
